/**
 * Members leaderboard — who posts the most, gets the most likes, is the most
 * active and has the most followers.
 *
 * Data comes from `GET /api/community/leaderboard?metric=posts|likes|active|followers&period=all|month|week`
 * (see Backend_API/routers/community.py). The admin-granted ✓ / VIP badges show
 * next to every name.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiAward, FiHeart, FiTrendingUp, FiUsers } from 'react-icons/fi';
import { cLeaderboard } from '../../api';
import { useLanguage } from '../../i18n';
import UserAvatar from '../../components/community/UserAvatar';
import UserBadges from '../../components/community/UserBadges';
import FollowButton from '../../components/community/FollowButton';

const METRICS = [
  { key: 'posts', icon: <FiAward className="w-4 h-4" />, label: 'lbPosts' },
  { key: 'likes', icon: <FiHeart className="w-4 h-4" />, label: 'lbLikes' },
  { key: 'active', icon: <FiTrendingUp className="w-4 h-4" />, label: 'lbActive' },
  { key: 'followers', icon: <FiUsers className="w-4 h-4" />, label: 'lbFollowers' },
];

const PERIODS = [
  { key: 'all', label: 'allTime' },
  { key: 'month', label: 'thisMonth' },
  { key: 'week', label: 'thisWeek' },
];

const MEDALS = ['🥇', '🥈', '🥉'];

export default function LeaderboardPage() {
  const { t } = useLanguage();
  const [metric, setMetric] = useState('posts');
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    cLeaderboard({ metric, period, limit: 30 })
      .then((res) => { if (alive) setData(res || { items: [] }); })
      .catch(() => { if (alive) setData({ items: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [metric, period]);

  /** “12 posts” / “34 ❤” / “56 points” / “78 followers” under the name. */
  const scoreText = (row) => {
    if (metric === 'likes') return `${row.score} ❤`;
    if (metric === 'followers') return `${row.score} ${t('followers')}`;
    if (metric === 'active') return `${row.score} ${t('points')} · ${row.posts} ${t('posts')} · ${row.comments} ${t('comments')}`;
    return `${row.score} ${t('posts')}`;
  };

  return (
    <div className="max-w-3xl mx-auto px-1 sm:px-0 space-y-4">
      <div className="rounded-2xl p-5 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm">
        <h1 className="text-xl font-extrabold flex items-center gap-2">
          <FiAward className="text-amber-500" /> {t('leaderboard')}
        </h1>
        <p className="mt-1 text-sm text-gray-500">{t('leaderboardDesc')}</p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMetric(m.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition ${
                metric === m.key
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {m.icon} {t(m.label)}
            </button>
          ))}
        </div>

        <div className="mt-2 flex flex-wrap gap-1.5">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPeriod(p.key)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border ${
                period === p.key
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700'
              }`}
            >
              {t(p.label)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
          ))}
        </div>
      ) : data.items.length === 0 ? (
        <p className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-8 text-center text-sm text-gray-400">
          {t('leaderboardEmpty')}
        </p>
      ) : (
        <ol className="space-y-2">
          {data.items.map((row) => (
            <li
              key={row.user.id}
              className={`flex items-center gap-3 rounded-2xl p-3 border shadow-sm ${
                row.rank <= 3
                  ? 'bg-gradient-to-r from-amber-50 to-white dark:from-gray-800 dark:to-gray-900 border-amber-100 dark:border-gray-700'
                  : 'bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-800'
              }`}
            >
              <span className="w-8 text-center font-extrabold text-sm text-gray-500 shrink-0">
                {MEDALS[row.rank - 1] || row.rank}
              </span>
              <UserAvatar user={row.user} size={44} to={`/u/${row.user.username}`} />
              <div className="min-w-0 flex-1">
                <Link
                  to={`/u/${row.user.username}`}
                  className="flex items-center gap-1.5 font-bold text-sm truncate hover:underline"
                >
                  <span className="truncate">{row.user.name || row.user.username}</span>
                  <UserBadges user={row.user} />
                </Link>
                <p className="text-[11px] text-gray-400 truncate">@{row.user.username}</p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{scoreText(row)}</p>
              </div>
              <FollowButton user={{ ...row.user, is_following: row.user.is_following }} />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
